import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, BookIcon, GraduationCapIcon, UserIcon, BuildingIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// School Administration
{
  id: 'sa1',
  title: 'School Registration',
  description: 'Register new educational institution or school',
  category: 'School Administration',
  estimatedTime: '15-20 business days',
  fee: '$500.00'
}, {
  id: 'sa2',
  title: 'Curriculum Approval',
  description: 'Submit curriculum for review and approval',
  category: 'School Administration',
  estimatedTime: '10-15 business days',
  fee: '$250.00'
}, {
  id: 'sa3',
  title: 'Teacher Certification',
  description: 'Process teacher certification and licensing',
  category: 'School Administration',
  estimatedTime: '7-10 business days',
  fee: '$150.00'
}, {
  id: 'sa4',
  title: 'Administrative Support',
  description: 'Access administrative resources and support services',
  category: 'School Administration',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
},
// Student Services
{
  id: 'ss1',
  title: 'Student Enrollment',
  description: 'Process new student enrollment applications',
  category: 'Student Services',
  estimatedTime: '5-7 business days',
  fee: '$25.00'
}, {
  id: 'ss2',
  title: 'Transportation Registration',
  description: 'Register for student transportation services',
  category: 'Student Services',
  estimatedTime: '3-5 business days',
  fee: 'Varies by distance'
}, {
  id: 'ss3',
  title: 'Special Education Support',
  description: 'Access special education resources and support',
  category: 'Student Services',
  estimatedTime: '7-10 business days',
  fee: 'No fee'
}, {
  id: 'ss4',
  title: 'Student Records Request',
  description: 'Request student transcripts and academic records',
  category: 'Student Services',
  estimatedTime: '3-5 business days',
  fee: '$15.00'
},
// Educational Programs
{
  id: 'ep1',
  title: 'After-School Programs',
  description: 'Register for after-school educational activities',
  category: 'Educational Programs',
  estimatedTime: '3-5 business days',
  fee: 'Varies by program'
}, {
  id: 'ep2',
  title: 'Summer Learning',
  description: 'Enroll in summer educational programs',
  category: 'Educational Programs',
  estimatedTime: '5-7 business days',
  fee: 'Varies by program'
}, {
  id: 'ep3',
  title: 'Adult Education',
  description: 'Access adult learning and continuing education programs',
  category: 'Educational Programs',
  estimatedTime: '3-5 business days',
  fee: 'Varies by course'
}, {
  id: 'ep4',
  title: 'Literacy Programs',
  description: 'Join community literacy and language programs',
  category: 'Educational Programs',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
},
// Facilities & Resources
{
  id: 'fr1',
  title: 'Facility Maintenance',
  description: 'Request school facility maintenance and repairs',
  category: 'Facilities & Resources',
  estimatedTime: '3-5 business days',
  fee: 'Based on service'
}, {
  id: 'fr2',
  title: 'Resource Distribution',
  description: 'Request educational materials and resources',
  category: 'Facilities & Resources',
  estimatedTime: '5-7 business days',
  fee: 'Based on materials'
}, {
  id: 'fr3',
  title: 'Technology Support',
  description: 'Access educational technology support services',
  category: 'Facilities & Resources',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'fr4',
  title: 'Library Services',
  description: 'Register for municipal library services and resources',
  category: 'Facilities & Resources',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'School Administration':
      return <GraduationCapIcon className="h-6 w-6 text-blue-600" data-id="element-3642" />;
    case 'Student Services':
      return <UserIcon className="h-6 w-6 text-green-600" data-id="element-3643" />;
    case 'Educational Programs':
      return <BookIcon className="h-6 w-6 text-purple-600" data-id="element-3644" />;
    case 'Facilities & Resources':
      return <BuildingIcon className="h-6 w-6 text-orange-600" data-id="element-3645" />;
    default:
      return <BookIcon className="h-6 w-6 text-gray-600" data-id="element-3646" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3647">
      <div className="p-5 flex flex-col h-full" data-id="element-3648">
        <div className="flex-1" data-id="element-3649">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3650">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3651">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3652">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3653">
            <div className="flex items-center" data-id="element-3654">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3655">
                ⏱
              </span>
              <span data-id="element-3656">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3657">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3658">
                💰
              </span>
              <span data-id="element-3659">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3660">
            Access Service
            <span className="ml-1" data-id="element-3661">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3662">
      <div className="flex items-center mb-4" data-id="element-3663">
        <CategoryIcon category={category} data-id="element-3664" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3665">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3666">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3667" />)}
      </div>
    </div>;
};
export const EducationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3668">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3669">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3670">
          <a href="/" className="hover:text-gray-700" data-id="element-3671">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3672" />
          <span className="text-gray-900" data-id="element-3673">Education Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3674">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3675">
            Education Services
          </h1>
          <p className="text-gray-600" data-id="element-3676">
            Access educational programs, student services, and school
            administration resources
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3677">
          <div className="max-w-md relative" data-id="element-3678">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3679">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3680" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3681" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3682" />)}
      </div>
    </div>;
};